/**
 * ChallanService — Axios-based service (migrated from panels-operations).
 * Handles the single Challan page item operations.
 */
import { getLoginUserEmail } from '@/lib/auth'
import { asRecord, encodeBase64Utf8 } from '@/utils/data'
import { api, buildQuery, encodeQueryValue, userTypeHeader } from './serviceUtils'

const normalizeManualAssignmentPayload = (payload: unknown) => {
  const data = asRecord(payload)
  const normalized: Record<string, unknown> = {
    challanItemIds: data.challanItemIds,
  }
  if (data.assignTo) normalized.assignTo = data.assignTo
  if (data.agentGroup) normalized.agentGroup = data.agentGroup
  return normalized
}

export type BulkAssignMode = 'AUTO' | 'MANUAL'

export const BULK_ASSIGN_MAX_ITEMS = 500

export type BulkSkipReason = 'NOT_FOUND' | 'NOT_OPEN' | 'OUT_OF_SCOPE'

export interface BulkSkippedItem {
  id: string
  reason: BulkSkipReason | string
  status?: string
}

export interface BulkAssignPayload {
  challanItemIds: string[]
  /** Raw pasted text; the backend splits it on commas, spaces, semicolons and newlines. */
  challanIdsText?: string
  mode: BulkAssignMode
  /** Account ids (preferred) or full emails; never the masked emails /ops-accounts returns. */
  assignees?: string[]
}

export interface BulkAssignResult {
  message?: string
  mode?: BulkAssignMode
  requested?: number
  assigned?: number
  skipped?: number
  skippedItems?: BulkSkippedItem[] | null
  perAssignee?: Record<string, number>
}

/** Server limit per request; larger sheets are sent in sequential chunks. */
export const BULK_CLOSE_MAX_ROWS = 100

/** Receipts are downloaded and re-uploaded server side, so big batches are slow. */
const BULK_CLOSE_TIMEOUT_MS = 5 * 60 * 1000

/** One sheet line; blank fields are left out. */
export interface BulkCloseRow {
  idChallanItem?: string
  tenantId?: string
  settlementAmount?: number
  statusRequested?: string
  remarks?: string
  pgCharges?: number
  agentFees?: number
  receiptUrl?: string
}

export type BulkCloseOutcome =
  | 'CLOSED'
  | 'WOULD_CLOSE'
  | 'ALREADY_PAID'
  | 'NOT_ON_PANEL'
  | 'NO_RECEIPT'
  | 'INVALID_RECEIPT'
  | 'NOT_CLOSABLE'
  | 'TENANT_MISMATCH'
  | 'INVALID_ROW'
  | 'DUPLICATE_IN_REQUEST'
  | 'FAILED'

export interface BulkCloseResultRow {
  /** 1-based position in the submitted rows. */
  row: number
  idChallanItem: string
  outcome: BulkCloseOutcome | string
  status?: string
  detail?: string
}

export interface BulkCloseSummary {
  total: number
  closed: number
  alreadyPaid: number
  notOnPanel: number
  failed: number
  byOutcome?: Record<string, number>
}

export interface BulkCloseResponse {
  dryRun: boolean
  summary: BulkCloseSummary
  results: BulkCloseResultRow[]
  closed?: BulkCloseResultRow[] | null
  alreadyPaid?: BulkCloseResultRow[] | null
  failed?: BulkCloseResultRow[] | null
  notOnPanel?: BulkCloseResultRow[] | null
}

export interface OpsAccountListParams {
  role?: string
  status?: string
  search?: string
  pageNumber?: number
  pageSize?: number
  managerId?: string
}

export interface ChallanExportParams {
  startDate?: string
  endDate?: string
  columns?: string[]
  sourceId?: string
  isExpress?: boolean
}

const submitPayment = (id: string, params: unknown) =>
  api.patch(`api/v1/challan-item/payment/${id}`, params, { headers: userTypeHeader() }).then((r) => r.data)

const fetchPaymentGatewayUrl = (id: string) =>
  api.get(`/api/v1/challan-item/payment-gateway-url/${id}`, { headers: userTypeHeader() }).then((r) => r.data)

export const ChallanService = {
  fetchDisposeChallanItems: (queryString = '') =>
    api.get(`/api/v1/challan-item${queryString.replace(/([?&])noticeNumber=/g, '$1q=')}`, { headers: userTypeHeader() }).then((r) => r.data),

  fetchChallanItemUploadProofFileLink: (id: string) =>
    api.get(`api/v1/challan-item/download/${id}`, { headers: userTypeHeader() }).then((r) => r.data),

  getDisposeProof: (id: string) =>
    api.get(`api/v1/challan-item/download/${id}`, { headers: userTypeHeader() }).then((r) => r.data),

  getGovernmentFeeProof: (id: string) =>
    api.get(`api/v1/challan-item/download/government-fee/${id}`, { headers: userTypeHeader() }).then((r) => r.data),

  getPaymentFailureProof: (id: string) =>
    api.get(`api/v1/challan-item/download/payment-url-failure/${id}`, { headers: userTypeHeader() }).then((r) => r.data),

  downloadReceipt: (id: string) =>
    api.get(`/api/v1/challan-item/download/receipt/${id}`, { headers: userTypeHeader() }).then((r) => r.data),

  fetchAllRegion: () =>
    api.get('api/v1/challan-region', { headers: userTypeHeader() }).then((r) => r.data),

  fetchAllCourts: () =>
    api.get('api/v1/challan-court/all', { headers: userTypeHeader() }).then((r) => r.data),

  fetchAllSources: () =>
    api.get('api/v1/challan-data/sources/all', { headers: userTypeHeader() }).then((r) => r.data),

  fetchChallanRegions: () =>
    api.get('api/v1/challan-region', { headers: userTypeHeader() }).then((r) => r.data),

  fetchCourtsByRegion: (id?: string) => {
    const url = id ? `api/v1/challan-region/${id}` : 'api/v1/challan-court/all'
    return api.get(url, { headers: userTypeHeader() }).then((r) => r.data)
  },

  fetchAgentsByCourt: () =>
    api.get('api/v1/challan-agent/all', { headers: userTypeHeader() }).then((r) => r.data),

  fetchAllAgents: () =>
    api.get('api/v1/challan-agent/all', { headers: userTypeHeader() }).then((r) => r.data),

  addAgent: (payload: unknown) =>
    api.post('api/v1/challan-agent/add', payload, { headers: userTypeHeader() }).then((r) => r.data),

  updateAgent: (payload: unknown) =>
    api.patch('api/v1/challan-agent/update', payload, { headers: userTypeHeader() }).then((r) => r.data),

  assignDisposerToChallanItem: (params: unknown) => {
    const payload = Array.isArray(params)
      ? { challanItemIds: params, assignTo: getLoginUserEmail() }
      : params
    return api.patch('api/v1/challan-item/assign', payload, { headers: userTypeHeader() }).then((r) => r.data)
  },

  assignAgentToChallanItem: (id: string, params: unknown) =>
    api.patch(`api/v1/challan-item/assignAgent/${id}`, params, { headers: userTypeHeader() }).then((r) => r.data),

  updateChallanItemAction: (id: string, params: unknown, action: string) =>
    api.patch(`api/v1/challan-item/${action}/${id}`, params, { headers: userTypeHeader() }).then((r) => r.data),

  submitPayment,
  resumeReattempt: (id: string) =>
    api.post(`/api/v1/challan-item/reattempt/resume/${encodeQueryValue(id)}`, undefined, { headers: userTypeHeader() }).then((r) => r.data),
  paymentChallanItem: submitPayment,

  uploadPaymentReceipt: (id: string, file: File) => {
    const payload = new FormData()
    payload.append('file', file)
    return api
      .patch(`api/v1/challan-item/receipt/${id}`, payload, { headers: userTypeHeader() })
      .then((r) => r.data)
  },

  validateRegNumber: (id: string, regNumber: string) =>
    api.get(`api/v1/challan-item/validate-reg-number/${id}?regNumber=${encodeQueryValue(regNumber)}`, { headers: userTypeHeader() }).then((r) => r.data),

  fetchAllOffenses: () =>
    api.get('api/v1/challan-data/offences/all', { headers: userTypeHeader() }).then((r) => r.data),

  addDelayReasonChallanItem: (id: string, payload: unknown) =>
    api.patch(`api/v1/challan-item/delay/${id}`, payload, { headers: userTypeHeader() }).then((r) => r.data),

  fetchDisposeUsers: () =>
    api.get('/api/v1/challan-data/users/dispose', { headers: userTypeHeader() }).then((r) => r.data),

  UpdateAssignTo: (id: string, assignToEmail: string) =>
    api
      .patch('api/v1/challan-item/admin/assign', { challanItemIds: [id], assignTo: assignToEmail }, { headers: userTypeHeader() })
      .then((r) => r.data),

  convertChallanVirtualCourtProceeding: (idChallanItem: string) =>
    api.patch(`api/v1/challan-item/vc/convert/${idChallanItem}`, {}, { headers: userTypeHeader() }).then((r) => r.data),

  fetchAllDelayReason: () =>
    api.get('/api/v1/challan-data/delay-reason/all', { headers: userTypeHeader() }).then((r) => r.data),

  fetchDisposeChallanDetailInfo: (id: string) =>
    api.get(`/api/v1/challan-item/details/${id}`, { headers: userTypeHeader() }).then((r) => r.data),

  getChallanItemTimeline: (id: string) =>
    api.get(`/api/v1/challan-item/timeline/${encodeQueryValue(id)}`, { headers: userTypeHeader() }).then((r) => r.data),

  resendWebhook: (id: string) =>
    api.post(`/api/v1/challan-item/resend-webhook/${encodeQueryValue(id)}`, undefined, { headers: userTypeHeader() }).then((r) => r.data),

  onExportData: (type: string, params: ChallanExportParams = {}) => {
    const query = new URLSearchParams()
    if (params.startDate) query.set('startDate', params.startDate)
    if (params.endDate) query.set('endDate', params.endDate)
    if (params.columns?.length) query.set('columns', params.columns.join(','))
    if (params.sourceId) query.set('sourceId', params.sourceId)
    if (typeof params.isExpress === 'boolean') query.set('isExpress', String(params.isExpress))
    const queryString = query.toString()
    return api.get(`/api/v1/${type}/export-csv${queryString ? `?${queryString}` : ''}`, {
      headers: userTypeHeader(),
      responseType: 'blob',
    })
  },

  fetchReasons: (type = '') =>
    api.get(`api/v1/challan-data/${type}/all`, { headers: userTypeHeader() }).then((r) => r.data),

  fetchBotRefundReasons: () =>
    api.get('api/v1/challan-data/bot-refund-reasons/all', { headers: userTypeHeader() }).then((r) => r.data),

  reOpenChallanItem: (id: string, reason: string) =>
    api.patch(`api/v1/challan-item/reopen/${id}`, { reason }, { headers: userTypeHeader() }).then((r) => r.data),

  updateAssignedDisposer: (id: string, payload: unknown) =>
    api.patch(`api/v1/challan-item/agentType/${id}`, payload, { headers: userTypeHeader() }).then((r) => r.data),

  bulkAssignChallanItems: (payload: BulkAssignPayload): Promise<BulkAssignResult> =>
    api
      .patch('api/v1/challan-item/assign-agent', {
        challanItemIds: payload.challanItemIds,
        ...(payload.challanIdsText?.trim() ? { challanIdsText: payload.challanIdsText } : {}),
        mode: payload.mode,
        ...(payload.mode === 'MANUAL' ? { assignees: payload.assignees ?? [] } : {}),
      }, { headers: userTypeHeader() })
      .then((r) => r.data),

  bulkCloseChallanItems: (rows: BulkCloseRow[], dryRun: boolean): Promise<BulkCloseResponse> =>
    api
      .post(`/api/v1/challan-item/bulk-close${dryRun ? '?dryRun=true' : ''}`, { rows }, {
        headers: userTypeHeader(),
        timeout: BULK_CLOSE_TIMEOUT_MS,
      })
      .then((r) => r.data?.data),

  fetchPaymentGatewayUrl,
  fetchPaymentRedirectionUrl: fetchPaymentGatewayUrl,
  regeneratePaymentUrl: (id: string) =>
    api.patch(`/api/v1/challan-item/regenerate-payment-url/${encodeQueryValue(id)}`, {}, { headers: userTypeHeader() }).then((r) => r.data),

  fetchVehicleIdentification: (appointmentOrderId: string, noticeNumber: string) =>
    api
      .get(`/api/v1/challan-item/vehicle-identification?appointmentOrderId=${encodeQueryValue(appointmentOrderId)}&noticeNumber=${encodeQueryValue(noticeNumber)}`, { headers: userTypeHeader() })
      .then((r) => r.data),

  fetchAgentGroups: () =>
    api.get('/api/v1/challan/cohort/agent/group', { headers: userTypeHeader() }).then((r) => r.data),

  manualChallanAssignment: (payload: unknown) =>
    api.post('/api/v1/challan-item/assign/manual', normalizeManualAssignmentPayload(payload), { headers: userTypeHeader() }).then((r) => r.data),

  fetchOpsAccounts: (params: OpsAccountListParams = {}) =>
    api.get(`/api/v1/ops-accounts${buildQuery(params as Record<string, unknown>)}`, { headers: userTypeHeader() }).then((r) => r.data),

  createOpsAccount: (payload: unknown) =>
    api.post('/api/v1/ops-accounts', payload, { headers: userTypeHeader() }).then((r) => r.data),

  updateOpsAccount: (id: string, payload: unknown) =>
    api.patch(`/api/v1/ops-accounts/${encodeQueryValue(id)}`, payload, { headers: userTypeHeader() }).then((r) => r.data),

  updateOpsAccountStatus: (id: string, status: 'ACTIVE' | 'INACTIVE') =>
    api.patch(`/api/v1/ops-accounts/${encodeQueryValue(id)}/status`, { status }, { headers: userTypeHeader() }).then((r) => r.data),

  updateOpsAccountWeight: (id: string, assignmentWeight: number) =>
    api.patch(`/api/v1/ops-accounts/${encodeQueryValue(id)}/weight`, { assignmentWeight }, { headers: userTypeHeader() }).then((r) => r.data),

  resetOpsAccountPassword: (id: string, newPassword?: string) =>
    api.patch(
      `/api/v1/ops-accounts/${encodeQueryValue(id)}/password`,
      newPassword ? { newPassword: encodeBase64Utf8(newPassword) } : {},
      { headers: userTypeHeader() },
    ).then((r) => r.data),
}
